import store, { browser, isSafari } from '@/store'
import moment from 'moment'
import {
	iconsByFileExt,
	mediaTypes,
	MAX_MESSAGE_MEDIA_SIZE_BYTES,
	IMAGE_MAX_WIDTH,
} from './constants'
import { redirectToLastActiveGroup } from '../helperMethods'
import QRious from 'qrious'
import ProntoStorage from './storage'
import debounce from 'debounce'
import sanitize from 'sanitize-html'
import { sortChatsByTitle } from '@/store/chats'
import { downloadFileByUrl } from './files'
import { trackAnalyticsEvent } from './firebase'
import posthog from 'posthog-js'

export const storage = new ProntoStorage('localStorage')
export const sStorage = new ProntoStorage('sessionStorage')

export const getLocalStorage = (key) => {
	const item = localStorage.getItem(key)
	try {
		return JSON.parse(item)
	} catch (e) {
		return item
	}
}

export const setLocalStorage = (key, item) => {
	localStorage.setItem(key, JSON.stringify(item))
	return getLocalStorage(key)
}

export const setLoggingParams = (path, params) => {
	path =
		params === ''
			? path
			: `${path}?info=${params}&t=${new Date().getTime()}&u=${
					store.auth.user.id
			  }&did=${store.auth.device_uuid}`
	window.history.pushState({}, null, path)
}

export const getUserName = (user) => {
	if (user.firstname && user.lastname) {
		return user.firstname + ' ' + user.lastname
	} else if (user.firstname && !user.lastname) {
		return user.firstname
	} else if (!user.firstname && !user.lastname) {
		return 'Unknown User'
	}
}

export function sortBubblesByTitle(a, b) {
	return sortChatsByTitle(a, b)
}

export const sortObjectsByProperty = (property) => (a, b) =>
	a[property] > b[property] ? 1 : -1

export const getIconByFileExt = (type) => {
	return iconsByFileExt[type] || iconsByFileExt.generic
}

export const showError = (error) => {
	return EventBus.$emit('showFailBanner', { error })
}

export const fileSize = (size, precision = 1) => {
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
	if (size === null || size === undefined) return '-- KB'
	if (size == 0) return '0 Bytes'
	const mb = browser.os === 'Mac OS' ? 1000 : 1024
	const i = parseInt(Math.floor(Math.log(size) / Math.log(mb)))
	const value = (size / Math.pow(mb, i)).toFixed(precision).replace('.0', '')
	return [value, sizes[i]].join(' ')
}

export const getFileSize = (size) => {
	if (size > MAX_MESSAGE_MEDIA_SIZE_BYTES) {
		showError("The file you're trying to upload has exceeded our 1GB limit...")
	} else {
		return fileSize(size)
	}
}

export const removeDuplicatesByField = (array, fieldName) => {
	if (!array || !fieldName) return null
	const mapperObj = array.reduce((acc, item, index) => {
		const key = item[fieldName]
		if (key !== null && key !== undefined) {
			acc[key] = { item, index }
		} else {
			acc[`non-mapped-${index}`] = { item, index }
		}
		return acc
	}, {})

	return Object.values(mapperObj)
		.sort((a, b) => a.index - b.index)
		.map((x) => x.item)
}

export const parseQueryParamArray = (arrStr) => {
	if (!arrStr) return null
	return arrStr
		.split(',')
		.map((id) => parseInt(id))
		.filter((x) => x)
}

export const copyNativeFiles = (files) => {
	if (!files.length) {
		files = [files]
	}
	return Array.from(files).map((f) =>
		['name', 'size', 'type'].reduce(
			(acc, key) => ({
				...acc,
				[key]: f[key],
			}),
			{}
		)
	)
}

export const middleEllipsis = (length, title) => {
	if (title.length > length) {
		let half = Math.floor(length / 2)
		var rem = title.length - half
		return (
			title.substring(0, half) + '...' + title.substring(title.length - half)
		)
	} else {
		return title
	}
}

export const moveItemInArray = (array, fromIndex, toIndex) => {
	const [item] = array.splice(fromIndex, 1)
	array.splice(toIndex, 0, item)
	return array
}

export const focusDirective = {
	mounted(el) {
		el.focus()
	},
}

export const safeHtmlDirective = {
	mounted(el, { value }) {
		el.innerHTML = sanitizeHTML(value)
	},
	updated(el, { value }) {
		el.innerHTML = sanitizeHTML(value)
	},
}

export const onClickOutsideDirective = {
	// stolen from: https://vueschool.io/lessons/click-outside-directive-1
	mounted(el, binding) {
		el.__clickOutsideHandler = (e) => {
			if (!el.contains(e.target)) {
				binding.value(e)
			}
		}
		document.body.addEventListener('click', el.__clickOutsideHandler)
	},
	unmounted(el) {
		document.body.removeEventListener('click', el.__clickOutsideHandler)
	},
}

export const getQueryParams = (searchString = location.search) => {
	return Object.fromEntries(new URLSearchParams(searchString))
}

export const sleep = (ms) => {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

export const copyToClipboard = (str) => {
	const el = document.createElement('textarea')
	el.value = str
	el.setAttribute('readonly', '')
	el.style.position = 'absolute'
	el.style.left = '-9999px'
	document.body.appendChild(el)
	el.select()
	document.execCommand('copy')
	document.body.removeChild(el)
}

export const parseJSON = (obj) => {
	try {
		return JSON.parse(obj)
	} catch (e) {
		return null
	}
}

export const getInitials = (user) => {
	return (
		[user.firstname, user.lastname]
			.filter((name) => name)
			.map((name) => name.replace(/[^\x00-\x7F]/g, ''))
			.map((name) => name[0])
			.join('') ?? ''
	)
}

export const normalizeMediaItem = (media) => {
	const type = media.type || media.mediatype
	const size = media.file_size || media.filesize
	const mimetype = media.mimetype || media.urlmimetype
	const isPhoto = type === mediaTypes.PHOTO
	const isVideo = type === mediaTypes.VIDEO
	const isVisual = isPhoto || isVideo
	const isAudio = type === mediaTypes.AUDIO
	const isFile = type === mediaTypes.FILE
	const thumbnail_url = media.thumbnail_url || media.thumbnail
	const local_id = media.local_id || media.uuid
	const copy = Object.assign({}, media, {
		type,
		local_id,
		uuid: local_id, // TODO: remove this once the migration is complete
		mediatype: type, // TODO: remove this once the migration is complete
		filesize: size, // TODO: remove this once the migration is complete
		mimetype,
		urlmimetype: mimetype, // TODO: remove this once the migration is complete
		mediaUrl: media.mediaUrl || store.mediaPath(media) || null,
		thumbnail_url,
		isPhoto,
		isVideo,
		isVisual,
		isAudio,
		isFile: isFile || (!isPhoto && !isVideo && !isAudio),
		size: fileSize(size),
	})
	if (!copy.uuid) {
		copy.uuid = crypto.randomUUID()
	}
	if (copy.isFile) {
		copy.extension = copy.title.substr(copy.title.lastIndexOf('.') + 1)
		copy.fileIcon = getIconByFileExt(copy.extension)
		copy.isPDF = copy.extension === 'pdf'
	}
	if (copy.isPhoto) {
		copy.isGif = mimetype?.endsWith('/gif') || false
		if (copy.isGif) {
			copy.thumbnailUrl = generateGiphyStillFromUrl(copy.mediaUrl)
		}
	}
	if (copy.thumbnail) {
		copy.thumbnailUrl = store.mediaThumbnailPath(copy)
	}
	if (copy.duration === null) {
		delete copy.duration
	}
	return copy
}

const customFields = [
	'isAudio',
	'isFile',
	'isGif',
	'isLastOfMore',
	'isPhoto',
	'isVideo',
	'isVisual',
	'isPDF',
	'isFailed',
	'fileIcon',
	'mediaUrl',
	'messageUUID',
	'progress',
	'file',
	'style',
	'isComplete',
	'thumbnailUrl',
	'size',
	'extension',
]
export const denormalizeMediaItem = (media) => {
	media = Object.assign({}, media)

	customFields.forEach((key) => {
		delete media[key]
	})
	return media
}

export const getMediaTypeByMimeType = (mimetype) => {
	if (!mimetype) return mediaTypes.FILE
	const [prefix] = mimetype.split('/')
	switch (prefix) {
		case 'image':
			return mediaTypes.PHOTO
		case 'video':
			return mediaTypes.VIDEO
		default:
			return mediaTypes.FILE
	}
}

export const getAsyncNativeFileImageDimensions = (file) => {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file)
		const img = new Image()
		img.onload = () => {
			const { width, height } = img
			resolve({ width, height })
		}
		img.onerror = (e) => {
			reject(e)
		}
		img.src = url
	})
}

export const getAsyncNativeFileVideoDimensions = (file) => {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file)
		const vid = document.createElement('video')
		vid.onloadedmetadata = () => {
			const { videoWidth: width, videoHeight: height } = vid
			resolve({ width, height })
		}
		vid.onerror = (e) => {
			reject(e)
		}
		vid.src = url
	})
}

let nativeFileTempId = 100
export const normalizeNativeFile = (file) => {
	if (!file) return null
	const mediatype = getMediaTypeByMimeType(file.type)
	const base = normalizeMediaItem({
		mediatype,
		title: file.name,
		filesize: file.size,
		mimetype: file.type,
	})
	if (base.isVisual) {
		base.mediaUrl = URL.createObjectURL(file)
	}
	return { ...base, file, tempId: nativeFileTempId++ }
}

export const normalizeGiphyMediaItem = (gifphyData) => {
	if (!gifphyData || !gifphyData.images) return null
	const { title, images } = gifphyData
	const { url, width, height, size } = images.downsized_medium
	// TODO: someday we should upload the still version as a thumbnail so we don't
	// have to generate it every time we display the image in the future
	// const { url: thumbnail } = images.downsized_still
	return normalizeMediaItem({
		title,
		url,
		width,
		height,
		urlmimetype: 'image/gif',
		filesize: parseInt(size),
		mediatype: mediaTypes.PHOTO,
		tempId: nativeFileTempId++,
	})
}

export const generateVideoThumbnailFromFile = (
	file,
	{ timestamp = 0.01, quality = 0.75, type = 'image/jpeg' } = {}
) => {
	if (!file) return Promise.reject(file)
	// Timestamp is defaulted to 0.01 because 0 returns a black thumbnail image for all videos
	// Modified from https://stackoverflow.com/a/63474748/1313904
	return new Promise((resolve, reject) => {
		try {
			const player = document.createElement('video')
			const canvas = document.createElement('canvas')
			function getUrlFromCanvas() {
				const ctx = canvas.getContext('2d')
				ctx.drawImage(player, 0, 0, canvas.width, canvas.height)
				ctx.canvas.toBlob(
					(blob) =>
						resolve({
							thumbnailUrl: URL.createObjectURL(blob),
							width: player.videoWidth,
							height: player.videoHeight,
							duration: player.duration,
						}),
					type,
					quality
				)
			}
			player.setAttribute('src', URL.createObjectURL(file))
			player.addEventListener('error', reject)
			player.addEventListener('loadedmetadata', () => {
				canvas.width = player.videoWidth
				canvas.height = player.videoHeight
				if (timestamp > 0) {
					if (player.duration < timestamp) {
						const msg = `${timestamp} is beyond video duration of ${player.duration}`
						reject(new Error(msg))
					} else {
						player.addEventListener('seeked', getUrlFromCanvas)
						if (isSafari) {
							setTimeout(() => {
								player.currentTime = timestamp
							}, 50)
						} else {
							player.currentTime = timestamp
						}
					}
				} else {
					getUrlFromCanvas()
				}
			})
			player.load()
		} catch (e) {
			reject(e)
		}
	})
}

export const generateGiphyStillFromUrl = (url) => {
	if (!url) return url
	// giphy hosts a still version of the animated gif, so let's use that
	const urlObj = new URL(url)
	if (
		urlObj.hostname.includes('pronto.io') ||
		urlObj.hostname.includes('app.vite')
	)
		return null
	if (!urlObj.hostname.includes('giphy.com')) return null
	const cleanUrl = urlObj.origin + urlObj.pathname
	const isMed = cleanUrl.includes('downsized-medium')
	const stillUrl = cleanUrl.replace(
		isMed ? 'downsized-medium.gif' : '.gif',
		isMed ? 'downsized_s.gif' : '_s.gif'
	)
	console.debug('[GIPHY]', 'generated still thumbnail url', { url, stillUrl })
	return stillUrl
}

export const generateGifThumbnailFromUrl = (
	url,
	{ quality = 0.75, type = 'image/jpeg' } = {}
) => {
	if (!url) return Promise.reject(url)
	const urlObj = new URL(url)
	if (urlObj.hostname.includes('giphy.com'))
		return generateGiphyStillFromUrl(url)
	// total hack to avoid CORS issues when doing local dev
	if (location.origin.includes('app.vite') && url.includes('pronto.io')) {
		url = location.origin + urlObj.pathname + urlObj.search
	} else if (urlObj.hostname.includes('giphy.com')) {
		const stillUrl = generateGiphyStillFromUrl(url)
		return Promise.resolve(stillUrl)
	}
	// Modified from generateVideoThumbnailFromFile()
	return new Promise((resolve, reject) => {
		const img = document.createElement('img')
		img.crossOrigin = 'Anonymous'
		const canvas = document.createElement('canvas')
		function onBlob(blob) {
			const thumbUrl = URL.createObjectURL(blob)
			resolve(thumbUrl)
		}
		function getUrlFromCanvas() {
			const ctx = canvas.getContext('2d')
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
			ctx.canvas.toBlob(onBlob, type, quality)
		}
		img.addEventListener('error', reject)
		img.addEventListener('load', () => {
			canvas.width = img.width
			canvas.height = img.height
			getUrlFromCanvas()
		})
		img.setAttribute('src', url)
	})
}

export const isDateISO = (dateVal) => {
	return dateVal.includes('Z') || dateVal.includes('T')
}

export const compareDates = (dateA, dateB) => {
	if (!dateA && !dateB) return 0 // both dates are invalid
	if (!dateA) return 1 // dateA is invalid, put dateB first
	if (!dateB) return -1 // dateB is invalid, put dateA first
	dateA = getLocalDate(dateA)
	dateB = getLocalDate(dateB)
	if (isNaN(dateA) && isNaN(dateB)) return 0 // both dates are invalid
	if (isNaN(dateA)) return 1 // dateA is invalid, put dateB first
	if (isNaN(dateB)) return -1 // dateB is invalid, put dateA first
	return dateB - dateA
}

export const getLocalDate = (dateVal) => {
	if (!dateVal) return null
	if (typeof dateVal === 'string') {
		return new Date(isDateISO(dateVal) ? dateVal : dateVal + 'Z')
	}
	return new Date(dateVal) // impossible to know the timezone for this integer
}

export const adjustWidthHeight = (w, h, container) => {
	if (!h || !w) return {}

	const breakpoint = 800
	let MAX_SIZE = IMAGE_MAX_WIDTH // Max width/height for the image
	let width = w
	let height = h

	const { offsetWidth } = container || {}

	// check container width to see if it's less than our max width
	if (window.innerWidth < breakpoint && MAX_SIZE > 300) {
		MAX_SIZE = 300
	} else if (offsetWidth && offsetWidth < MAX_SIZE) {
		MAX_SIZE = offsetWidth
	}

	const imgMax = Math.max(w, h)
	const isLandscape = w === imgMax
	const isPortrait = h === imgMax
	if (isLandscape && w > MAX_SIZE) {
		// image is wider than MAX_SIZE
		width = MAX_SIZE
		const ratio = MAX_SIZE / w
		height = h * ratio
	} else if (isPortrait && h > MAX_SIZE) {
		// image is taller than MAX_SIZE
		height = MAX_SIZE
		const ratio = MAX_SIZE / h
		width = w * ratio
	}

	return { width, height }
}

export const adjustMediaSize = (media, container) => {
	if (!media) return {}
	return adjustWidthHeight(media.width, media.height, container)
}

export const userPrefersReducedMotion = () => {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export const dayOfWeek = (date) => {
	const DOW = moment(date).calendar(null, {
		lastDay: '[YESTERDAY]',
		sameDay: '[TODAY]',
		nextDay: '[TOMORROW]',
	})
	return ['TODAY', 'TOMORROW', 'YESTERDAY'].includes(DOW)
		? DOW
		: moment(date).format('dddd').toUpperCase()
}

export const MomentPlugin = {
	install: function (app) {
		Object.assign(app.config.globalProperties, { $m: moment })
	},
}

export const EMAIL_REGEX = /^[^ @]+@[^ @]+\.[^ @]+$/
export const isValidEmail = (emailStr) => {
	return EMAIL_REGEX.test(emailStr)
}

export const getTimeAgoUnit = (dateStr) => {
	// see ranges here: https://momentjs.com/docs/#/displaying/fromnow/
	const mom = moment(dateStr)
	// 'a few seconds ago' ==> '1s'
	// 'spoke with Ben on 02/14/22 and we're not showing less than 1m anywhere in the app, so I'm changing this from s to m.  In the event we show seconds in the future we can set this back.
	const secsSince = Math.abs(moment().diff(mom, 'seconds'))
	if (secsSince >= 0 && secsSince <= 44) {
		return 'm'
	}
	const unit = mom.fromNow(true).split(' ')[1]
	// '4 months ago' ==> '4mo'
	const daysSince = Math.abs(moment().diff(mom, 'days'))
	if (daysSince >= 26 && daysSince <= 319) {
		return unit.substr(0, 2)
	}
	return unit.substring(0, 1)
}

export const getTimeAgoValue = (dateStr) => {
	// see ranges here: https://momentjs.com/docs/#/displaying/fromnow/
	const mom = moment(dateStr)
	// 'a few seconds ago' ==> '1s'
	const secsSince = moment().diff(mom, 'seconds')
	if (secsSince >= 0 && secsSince <= 44) {
		return 1
	}
	const value = mom.fromNow(true).split(' ')[0]
	if (['a', 'an', 'un', 'una', 'um', 'uma'].includes(value)) {
		// moment sucks and displays 'a month ago'/'a second ago' whenever value is 1
		// so we have to account for that here in 'en', 'es' and 'pt' locales
		return 1
	}
	return parseInt(value)
}

export const getTimeAgoShorthand = (dateStr) => {
	// 'hace 5 minutos' ==> '5 minutos' ==> '5m'
	const value = getTimeAgoValue(dateStr)
	const unit = getTimeAgoUnit(dateStr)
	return `${value}${unit}`
}

export const canRetryAPIError = (error) => {
	const status = parseInt(error?.response?.status)
	if (status >= 400 && status < 500) return false
	return true
}

export const addEventBusEvent = (event, handler) => {
	EventBus.$on(event, handler)
	return () => EventBus.$off(event, handler)
}

export const addEventBusEvents = (eventObject) => {
	if (!eventObject) return
	const removals = Object.entries(eventObject).map(([event, handler]) => {
		if (event.split && event.includes('|')) {
			// allow multiple events to be registered with a single
			// event key by chaining them with '|'
			event = event.split('|').map((e) => e.trim())
		}
		return addEventBusEvent(event, handler)
	})
	return () => removals.forEach((handler) => handler())
}

export const downloadFile = async (url, filename) => {
	return await downloadFileByUrl(url, filename)
}

export const EventBusPlugin = {
	install: function (app) {
		app.mixin({
			created() {
				this._eventBusEvents = []
			},
			beforeUnmount() {
				this._eventBusEvents.forEach((event) => event.off())
			},
			methods: {
				$onEventBus(...args) {
					if (typeof args[0] === 'object') {
						return this._addEventBusEventsByObj(args[0])
					}
					return this._addSingleEventBusEvent(...args)
				},

				_addSingleEventBusEvent(event, handler, debounceMs = 0) {
					if (event.split && event.includes('|')) {
						// allow multiple events to be registered with a single
						// event key by chaining them with '|'
						event = event.split('|').map((e) => e.trim())
					}
					if (debounceMs > 0) {
						handler = debounce(handler, debounceMs)
					}
					window.EventBus.$on(event, handler)
					const off = () => window.EventBus.$off(event, handler)
					this._eventBusEvents.push({ event, handler, off })
					return off
				},

				_addEventBusEventsByObj(eventObject) {
					if (!eventObject) return
					const offHandlers = Object.entries(
						eventObject
					).map(([event, handler]) =>
						this._addSingleEventBusEvent(event, handler)
					)
					return () => offHandlers.forEach((off) => off())
				},
			},
		})
	},
}

const keysToPersist = [
	'userKey',
	'api_token',
	'expandedCategories',
	'persist',
	'acceptedtos',
	'user',
	'device_uuid',
	'pronto_dev_tools',
]
export const clearAppStorage = async (keys = keysToPersist) => {
	// grab all of the data that we want to persist from localStorage
	const values = keys.map((key) => localStorage.getItem(key))
	// clear everything out of localStorage entirely
	localStorage.clear()
	// now put all of the persisted keys back
	keys.forEach((key, i) => localStorage.setItem(key, values[i]))
	// delete the entire database
	await store.dbDeleteDatabase()
}
export const clearAppStorageAndRefresh = async (keys) => {
	await clearAppStorage(keys)
	// go to a chat route so things reload as expected
	redirectToLastActiveGroup()
	// after route as resolved, then reload the browser
	setTimeout(() => window.location.reload(), 500)
}

export const generateQRCodeUrl = (value, options = {}) => {
	const qr = new QRious({
		value,
		size: 200,
		level: 'H',
		...options,
	})
	return qr.toDataURL('image/jpeg')
}

export const checkForRedirectToDownload = () => {
	const { hostname, pathname } = window.location
	if (
		hostname.startsWith('app.') &&
		hostname.includes('pronto.io') &&
		!pathname.startsWith('/invites')
	) {
		window.location = 'https://pronto.io/download'
		return true
	}
	return false
}

export const escapeHtml = (htmlStr) => {
	const textNode = document.createTextNode(htmlStr)
	const p = document.createElement('p')
	p.appendChild(textNode)
	return p.innerHTML
}

export const sanitizeHTML = (htmlStr, options = {}) => {
	return sanitize(htmlStr, {
		disallowedTagsMode: 'escape',
		allowedTags: sanitize.defaults.allowedTags.concat([
			'pronto_hl',
			'svg',
			'g',
			'defs',
			'linearGradient',
			'stop',
			'circle',
			'path',
		]),
		allowedAttributes: {
			...sanitize.defaults.allowedAttributes,
			['*']: ['class'],
			...(options.allowedAttributes || {}),
			svg: ['viewBox', 'xmlns'],
			path: ['d'],
		},
		parser: {
			lowerCaseTags: false,
			lowerCaseAttributeNames: false,
		},
		...options,
	})
}

export const safeStringify = (obj, indent = 0) => {
	let cache = []
	const retVal = JSON.stringify(
		obj,
		(key, value) =>
			typeof value === 'object' && value !== null
				? cache.includes(value)
					? undefined // Duplicate reference found, discard key
					: cache.push(value) && value // Store value in our collection
				: value,
		indent
	)
	cache = null
	return retVal
}

export const trackEvent = (name, data) => {
	trackAnalyticsEvent(name, data)
	posthog.capture(name, data)
}

export const isWrapper = () => {
	if (window.appConfig?.wrapper) return true
	const params = getQueryParams()
	return !!params.wrapper
}
